import {
  ConfirmResponse,
  useConfirmDialog
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Guid from "_shared/guids/guid";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { useRemoveNetworkConnectionMutation } from "users/networking/connections/_api/user-network-connection-api";
import useNetworkConnectionStatus from "users/networking/connections/_hooks/use-network-connection-status";
import { getNetworkConnectionByUserId } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkInvitationAPIRequest from "users/networking/connections/invitations/_api/commands/network-invitation-api-command";
import {
  useAcceptNetworkInvitationMutation,
  useCancelNetworkInvitationMutation,
  useCreateNetworkInvitationMutation,
  useGetUserNetworkInvitationsQuery,
  useRejectNetworkInvitationMutation
} from "users/networking/connections/invitations/_api/network-invitation-api";
import NetworkInvitationQuery from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import { NetworkInvitationInfoJSON } from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";
import { getNetworkInvitationByUserId } from "users/networking/connections/invitations/_selectors/network-invitation-selectors";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";
import { NetworkStatus } from "users/networking/connections/network-status";

export { NetworkStatus } from "users/networking/connections/network-status";

/**
 * Where the session user stands with one other user, and the moves that change it.
 *
 * Pulled out of `NetworkButton` so a caller that lays out its own controls — a marketplace result
 * card puts "Add to network" in its footer and "Remove from network" in a menu — reads the same
 * status and calls the same mutations as the profile page's single button. One instance per card:
 * the invitation and connection a mutation returns are held locally until the queries catch up, so
 * two instances for one user would disagree for that moment.
 */
export type NetworkConnection = {
  /** False when the session may not use the network at all; offer nothing then. */
  isAvailable: boolean;
  isSaving: boolean;
  status: NetworkStatus;
  acceptInvitation: () => Promise<void>;
  cancelInvitation: () => Promise<void>;
  rejectInvitation: () => Promise<void>;
  removeConnection: () => Promise<void>;
  /** Resolves true once the invitation is sent, so a caller knows it may close its form. */
  sendInvitation: (message: string) => Promise<boolean>;
};

export default function useNetworkConnection(userId: Guid): NetworkConnection {
  // #region State
  const [localInvitation, setLocalInvitation] = React.useState<{
    userId: string;
    invitation: NetworkInvitationInfoJSON;
  } | null>(null);
  const [localAcceptedConnection, setLocalAcceptedConnection] = React.useState<{
    userId: string;
    connectionId: Guid;
  } | null>(null);
  const activeLocalInvitation = localInvitation?.userId === userId.value ? localInvitation.invitation : null;
  const activeLocalAcceptedConnection = localAcceptedConnection?.userId === userId.value ? localAcceptedConnection : null;

  // #region Hooks
  const confirm = useConfirmDialog();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();
  const userPermissions = useSelector(getSessionPermissions);

  // #region Queries
  const invitationQuery = React.useMemo(() => new NetworkInvitationQuery({}), []);
  useGetUserNetworkInvitationsQuery(invitationQuery);
  const isSelf = userId.isEqualTo(session.user?.id);
  // One person, not the full list: the server pages the list, so a person can be a connection
  // without being on any page this session has loaded.
  const isNetworked = useNetworkConnectionStatus(userId, { skip: isSelf }) === true;
  const networkConnection = useSelector(getNetworkConnectionByUserId(userId));
  const selectedNetworkInvitation = useSelector(getNetworkInvitationByUserId(userId));

  const networkInvitation = React.useMemo(() => {
    if (selectedNetworkInvitation) return selectedNetworkInvitation;
    return activeLocalInvitation ? NetworkInvitationInfo.fromJSON(activeLocalInvitation) : undefined;
  }, [activeLocalInvitation, selectedNetworkInvitation]);

  // #region Mutations
  const [removeConnectionMutation, removeConnectionResult] = useRemoveNetworkConnectionMutation();
  const [createInvitation, createInvitationResult] = useCreateNetworkInvitationMutation();
  const [acceptInvitationMutation, acceptInvitationResult] = useAcceptNetworkInvitationMutation();
  const [cancelInvitationMutation, cancelInvitationResult] = useCancelNetworkInvitationMutation();
  const [rejectInvitationMutation, rejectInvitationResult] = useRejectNetworkInvitationMutation();

  const isSaving: boolean = createInvitationResult.isLoading
    || acceptInvitationResult.isLoading
    || cancelInvitationResult.isLoading
    || rejectInvitationResult.isLoading
    || removeConnectionResult.isLoading;

  function getNetworkStatus(): NetworkStatus {
    if (isSelf) {
      return NetworkStatus.Self;
    }
    if (isNetworked || activeLocalAcceptedConnection) {
      return NetworkStatus.InNetwork;
    }
    if (networkInvitation) {
      if (networkInvitation.status === "rejected") return NetworkStatus.Rejected;
      if (networkInvitation.status === "open") {
        return networkInvitation.isInviter ? NetworkStatus.Sent : NetworkStatus.Received;
      }
    }
    return NetworkStatus.None;
  }

  async function sendInvitation(message: string): Promise<boolean> {
    if (userId.isEqualTo(session.user?.id)) return false;
    const invitationData: NetworkInvitationAPIRequest = {
      recipientId: userId.value,
      message: message
    };
    try {
      const result = await createInvitation(invitationData).unwrap();
      setLocalInvitation({ userId: userId.value, invitation: result });

      enqueueSnackbar("Sent network invitation", { variant: "success" });
      return true;
    } catch {
      enqueueSnackbar("Couldn't send network request. Please try again.", {
        variant: "error",
      });
      return false;
    }
  }

  async function removeConnection() {
    const response = await confirm({
      title: "Remove from network?",
      message: "Are you sure you want to remove this user from your network?",
      okButtonText: "Remove",
      cancelButtonText: "Cancel",
    });
    if (response == ConfirmResponse.Cancel) return;

    // Read before the `try`: a `throw`, `?.` or `??` inside one keeps the React Compiler from
    // compiling the hook at all.
    const connectionId = networkConnection?.connectionId ?? activeLocalAcceptedConnection?.connectionId;
    if (!connectionId) {
      enqueueSnackbar("Couldn't remove from network. Please try again.", { variant: "error" });
      return;
    }

    try {
      await removeConnectionMutation({ connectionId }).unwrap();
      setLocalAcceptedConnection(null);

      enqueueSnackbar("Removed from network", { variant: "info" });
    } catch {
      enqueueSnackbar("Couldn't remove from network. Please try again.", {
        variant: "error",
      });
    }
  }

  async function cancelInvitation() {
    try {
      if (!networkInvitation) return;

      await cancelInvitationMutation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);

      enqueueSnackbar("Canceled network invitation", { variant: "info" });
    } catch {
      enqueueSnackbar("Couldn't cancel network request. Please try again.", {
        variant: "error",
      });
    }
  }

  async function acceptInvitation() {
    try {
      if (!networkInvitation) return;

      const connection = await acceptInvitationMutation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);
      setLocalAcceptedConnection({
        userId: userId.value,
        connectionId: new Guid(connection.id),
      });
    } catch {
      enqueueSnackbar("Couldn't accept network request. Please try again.", {
        variant: "error",
      });
    }
  }

  async function rejectInvitation() {
    try {
      if (!networkInvitation) return;

      await rejectInvitationMutation({ invitationId: networkInvitation.id }).unwrap();
      setLocalInvitation(null);

      enqueueSnackbar("Rejected network invitation", { variant: "info" });
    } catch {
      enqueueSnackbar("Couldn't reject network request. Please try again.", {
        variant: "error",
      });
    }
  }

  return {
    isAvailable: Boolean(userPermissions?.includesAccessToNetwork),
    isSaving,
    status: getNetworkStatus(),
    acceptInvitation,
    cancelInvitation,
    rejectInvitation,
    removeConnection,
    sendInvitation,
  };
}
